import { type Query, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { ServerEvent } from "../../shared/events";
import { withSession } from "./api";
import { isSaving } from "./saving";

export type ConnectionState = "connecting" | "open" | "closed";

/**
 * A query that updates itself record by record (its meta.onChange, e.g. the Price List: liveRows.ts)
 * is told which records changed (or "all") instead of being refetched.
 */
type OnChange = (change: Record<string, string[]> | "all") => void;
const onChangeOf = (q: Query) => q.meta?.onChange as OnChange | undefined;

// Data with a save in flight isn't refetched here: the save refetches it when it's done (lib/saving.ts).
function handle(qc: QueryClient, event: ServerEvent) {
    switch (event.type) {
        case "records.changed": {
            const shows = (q: Query) => q.queryKey[0] === "c" && q.queryKey[1] === event.companyId && ((q.meta?.sm8 as string[] | undefined)?.includes(event.object) ?? false);
            for (const q of qc.getQueryCache().findAll({ predicate: shows })) onChangeOf(q)?.(event.uuids.length ? { [event.object]: event.uuids } : "all");
            void qc.invalidateQueries({ predicate: (q) => shows(q) && !onChangeOf(q) && !isSaving(q.queryKey) });
            break;
        }
        case "sync.changed":
            void qc.invalidateQueries({ queryKey: ["c", event.companyId, "sync"] });
            break;
        case "company.changed":
            // Includes access changes, which can change what any tool shows (e.g. whether it's editable).
            void qc.invalidateQueries({ queryKey: ["me"] });
            void qc.invalidateQueries({ queryKey: ["c", event.companyId] });
            break;
        case "me.changed":
            void qc.invalidateQueries({ queryKey: ["me"] });
            break;
        case "invalidate":
            for (const q of qc.getQueryCache().findAll({ queryKey: ["c", event.companyId, ...event.queryKey] })) onChangeOf(q)?.(event.records ?? "all");
            void qc.invalidateQueries({ queryKey: ["c", event.companyId, ...event.queryKey], predicate: (q) => !onChangeOf(q) && !isSaving(q.queryKey) });
            // Queries that show another tool's data (useSm8Query's `alsoOn`).
            void qc.invalidateQueries({
                predicate: (q) =>
                    q.queryKey[0] === "c" &&
                    q.queryKey[1] === event.companyId &&
                    ((q.meta?.alsoOn as unknown[] | undefined)?.includes(event.queryKey[0]) ?? false) &&
                    !isSaving(q.queryKey),
            });
            break;
    }
}
/** How often the socket is pinged, and how long past that it waits for something back before it counts as dead. */const PING_MS = 30_000;const PONG_WAIT_MS = 10_000;

/**
 * Keeps a websocket open while signed in and turns server events into query invalidations,
 * so every open tab/device refetches whatever changed. Reconnects with backoff. Each time it opens (the first time too: a page
 * loads its data before its socket is open, a second or two later), what was loaded before then is refetched, in case events
 * were missed meanwhile.
 * `channelKey` should change when the user's company list changes, so subscriptions update.
 */
export function useRealtime(enabled: boolean, channelKey: string): ConnectionState {
    const qc = useQueryClient();
    const [state, setState] = useState<ConnectionState>("closed");

    useEffect(() => {
        if (!enabled) return;
        let ws: WebSocket | undefined;
        let retry = 0;
        let stopped = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let ping: ReturnType<typeof setInterval> | undefined;

        const connect = () => {
            setState("connecting");
            // withSession: inside ServiceM8 the cookie is blocked, so the session goes in the URL.
            const socket = new WebSocket(withSession(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`));
            ws = socket;
            // When it last heard anything: a socket that's stopped answering its pings (a phone woken, the network changed) is dead
            // though it still says it's open, so it's closed, and so reconnected.
            let heard = Date.now();
            socket.addEventListener("open", () => {
                // Loaded before it opened (or while it was down): anything changed meanwhile was never told.
                void qc.invalidateQueries({ predicate: (q) => q.state.dataUpdatedAt > 0 });
                retry = 0;
                heard = Date.now();
                setState("open");
                ping = setInterval(() => {
                    if (socket.readyState !== WebSocket.OPEN) return;
                    if (Date.now() - heard > PING_MS + PONG_WAIT_MS) socket.close();
                    else socket.send("ping");
                }, PING_MS);
            });
            socket.addEventListener("message", (e) => {
                heard = Date.now();
                if (e.data === "pong") return;
                handle(qc, JSON.parse(e.data as string) as ServerEvent);
            });
            socket.addEventListener("close", () => {
                clearInterval(ping);
                // An old socket closing after a new one opened (another business added): it says nothing about the new one.
                if (stopped || ws !== socket) return;
                setState("closed");
                timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** retry++));
            });
        };
        connect();

        // Phones suspend sockets in the background; reconnect immediately when the tab returns.
        const onVisible = () => {
            if (document.visibilityState === "visible" && ws?.readyState === WebSocket.CLOSED) {
                clearTimeout(timer);
                connect();
            }
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            stopped = true;
            clearTimeout(timer);
            clearInterval(ping);
            document.removeEventListener("visibilitychange", onVisible);
            ws?.close();
        };
    }, [enabled, channelKey, qc]);

    return state;
}
